import { trpc } from "@/lib/trpc";
import { UNAUTHED_ERR_MSG } from '@shared/const';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink, TRPCClientError } from "@trpc/client";
import { createRoot } from "react-dom/client";
import superjson from "superjson";
import App from "./App";
import { getLoginUrl } from "./const";
import "./index.css";
import { LanguageProvider } from "./contexts/LanguageContext";

const queryClient = new QueryClient();

// Keep the document language explicit while allowing browser translation tools
// (including Google Translate) to translate the English information platform.
if (typeof document !== "undefined") {
  document.documentElement.lang = "en";
}

const redirectToLoginIfUnauthorized = (error: unknown) => {
  if (!(error instanceof TRPCClientError)) return;
  if (typeof window === "undefined") return;

  const isUnauthorized = error.message === UNAUTHED_ERR_MSG;

  if (!isUnauthorized) return;

  window.location.href = getLoginUrl();
};

queryClient.getQueryCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.query.state.error;
    redirectToLoginIfUnauthorized(error);
    console.error("[API Query Error]", error);
  }
});

queryClient.getMutationCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.mutation.state.error;
    redirectToLoginIfUnauthorized(error);
    console.error("[API Mutation Error]", error);
  }
});

const trpcClient = trpc.createClient({
  links: [
    httpBatchLink({
      url: "/api/trpc",
      transformer: superjson,
      fetch(input, init) {
        // 附加 JWT token header（绕过 cookie SameSite 限制）
        const adminToken = localStorage.getItem("admin-jwt-token");
        const userToken = localStorage.getItem("user-jwt-token");
        const headers: Record<string, string> = {};
        if (adminToken) {
          headers["x-admin-token"] = adminToken;
        }
        if (userToken && !adminToken) {
          // 只有在没有 admin token 时才发送 user token，避免混淆
          headers["x-user-token"] = userToken;
        }
        return globalThis.fetch(input, {
          ...(init ?? {}),
          credentials: "include",
          headers: {
            ...(init?.headers ?? {}),
            ...headers,
          },
        });
      },
    }),
  ],
});

// Analytics and Web Vitals are not required to render the public shell. Start them
// after the page load event so tracking cannot sit in the first navigation waterfall.
const scheduleAnalyticsInitialization = () => {
  const initialize = async () => {
    const [{ initializeVisitorTracking }, { initializeWebVitals }] = await Promise.all([
      import("./lib/visitorTracking"),
      import("./lib/webVitals"),
    ]);
    initializeVisitorTracking().catch(err => console.error("Failed to initialize visitor tracking:", err));
    initializeWebVitals();
  };

  if (typeof window === "undefined") return;
  const afterLoad = () => window.setTimeout(initialize, 2000);
  if (document.readyState === "complete") {
    afterLoad();
  } else {
    window.addEventListener("load", afterLoad, { once: true });
  }
};

scheduleAnalyticsInitialization();

// Animation utilities are non-critical for the first paint. Load them during the
// first idle window so the global stylesheet can stay on the critical path only.
const scheduleAnimationStyles = () => {
  const load = () => import("./styles/animations.css");
  if (typeof window !== "undefined" && "requestIdleCallback" in window) {
    window.requestIdleCallback(load, { timeout: 2000 });
  } else {
    setTimeout(load, 1000);
  }
};

scheduleAnimationStyles();

createRoot(document.getElementById("root")!).render(
  <trpc.Provider client={trpcClient} queryClient={queryClient}>
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <App />
      </LanguageProvider>
    </QueryClientProvider>
  </trpc.Provider>
);

// The critical shell lives outside #root so React never tries to reconcile or
// remove a node that an inline script already removed. Once the app is mounted,
// remove the shell safely from its separate DOM container.
if (typeof document !== "undefined") {
  const shell = document.getElementById("critical-home-shell");
  if (window.location.pathname === "/") {
    window.setTimeout(() => shell?.remove(), 1500);
  } else {
    shell?.remove();
  }
}
